// Stock/inventory movement logic — deduction on shipment, restoration on
// cancel/return, and reconciliation when an order's items are edited after
// stock was already deducted. This is the "inventory idempotency" logic the
// original refactor spec singled out for protection (deducting must never
// double-count, editing a shipped order must reconcile the delta rather
// than re-deduct from scratch, a return must be reversible).
//
// Extracted from public/index.html's inline script (Phase 2 modularization,
// slice 3). Like pricing.js, these take their data (products, packaging
// items, movement history, current user) as explicit arguments instead of
// closing over the app's mutable PRODUCTS/SETTINGS globals — real
// dependency injection, independently unit-tested
// (tests/unit/inventory.test.mjs). public/index.html keeps a one-line
// wrapper of the same original name for every function here, so every
// existing call site keeps working unchanged. See
// REFACTOR_SECURITY_REPORT.md, Phase 2 section, for the behavioral-
// equivalence checks run before each function was removed from the inline
// script.
import { getOrderItems, productForItem } from './pricing.js';

export const INVENTORY_TRIGGER_STATUSES = ['envoyee', 'transit', 'livree'];

export function inventoryIsTriggered(status) {
  return INVENTORY_TRIGGER_STATUSES.includes(status);
}

export function stockMeta(item) {
  const stock = Number(item && item.stock || 0), low = Math.max(0, Number(item && item.lowStock || 0));
  return stock <= 0 ? { key: 'out', label: 'Rupture' } : (stock <= low ? { key: 'low', label: 'Faible' } : { key: 'ok', label: 'Disponible' });
}

export function inventoryAlertCount(products, packagingItems) {
  return products.filter(p => stockMeta(p).key !== 'ok').length + packagingItems.filter(p => stockMeta(p).key !== 'ok').length;
}

function findItem(kind, id, products, packagingItems) {
  return kind === 'product' ? products.find(p => p.id === id) : packagingItems.find(p => p.id === id);
}

// Mutates `history` in place (unshift), matching the original's mutation of
// SETTINGS.inventoryHistory — callers pass SETTINGS.inventoryHistory itself.
export function recordInventoryMovement(history, user, kind, item, delta, reason, before, after, movementType) {
  history.unshift({
    id: 'mov_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5),
    kind, itemId: item.id, name: item.name, delta: Number(delta), before: Number(before), after: Number(after),
    movementType: movementType || (Number(delta) >= 0 ? 'entry' : 'exit'),
    reason: reason || 'Ajustement manuel', at: new Date().toISOString(), user: user || 'Admin'
  });
}

// Mutates `order`, matched products/packagingItems (stock fields), and
// `history` in place — same mutation shape as the original.
export function consumeInventoryForOrder(order, previousStatus, isNew, products, packagingItems, history, user) {
  if (!order || order.inventoryDeducted || !inventoryIsTriggered(order.status) || (!isNew && inventoryIsTriggered(previousStatus))) return { deducted: false, warnings: [] };
  const warnings = [], snapshot = { products: [], packaging: [] }, reason = 'Commande ' + (order.orderNo || order.id);
  getOrderItems(products, order).forEach(line => {
    const product = productForItem(products, line), qty = Math.max(1, Number(line.qty || 1));
    if (!product) { if (line.product) warnings.push('Produit introuvable : ' + line.product); return; }
    line.productId = product.id; line.product = product.name;
    const before = Number(product.stock || 0), after = before - qty;
    product.stock = after;
    recordInventoryMovement(history, user, 'product', product, -qty, reason, before, after);
    snapshot.products.push({ id: product.id, name: product.name, qty });
    if (after < 0) warnings.push(product.name + ' : manque ' + Math.abs(after));
  });
  packagingItems.forEach(item => {
    const qty = Math.max(0, Number(item.usagePerOrder || 0));
    if (!qty) return;
    const before = Number(item.stock || 0), after = before - qty;
    item.stock = after;
    recordInventoryMovement(history, user, 'packaging', item, -qty, reason, before, after);
    snapshot.packaging.push({ id: item.id, name: item.name, qty });
    if (after < 0) warnings.push(item.name + ' : manque ' + Math.abs(after));
  });
  order.inventoryDeducted = true; order.inventoryDeductedAt = new Date().toISOString(); order.inventorySnapshot = snapshot;
  return { deducted: true, warnings };
}

export function restoreInventoryForOrder(order, reason, products, packagingItems, history, user) {
  if (!order?.inventoryDeducted || !order.inventorySnapshot) return { restored: false };
  const why = reason || ('Annulation ' + (order.orderNo || order.id));
  [...(order.inventorySnapshot.products || []), ...(order.inventorySnapshot.packaging || [])].forEach(s => {
    const kind = (order.inventorySnapshot.products || []).includes(s) ? 'product' : 'packaging';
    const item = findItem(kind, s.id, products, packagingItems);
    if (!item) return;
    const before = Number(item.stock || 0), after = before + Number(s.qty || 0);
    item.stock = after;
    recordInventoryMovement(history, user, kind, item, Number(s.qty || 0), why, before, after, 'entry');
  });
  order.inventoryDeducted = false; order.inventoryRestoredAt = new Date().toISOString(); order.inventoryRestoredReason = why;
  return { restored: true };
}

export function reconcileDeductedInventory(existing, updated, products, history, user) {
  if (!existing?.inventoryDeducted) return { reconciled: false };
  const oldMap = new Map((existing.inventorySnapshot?.products || []).map(x => [String(x.id), Number(x.qty || 0)])), newMap = new Map();
  getOrderItems(products, updated).forEach(line => {
    const p = productForItem(products, line);
    if (!p) return;
    line.productId = p.id; line.product = p.name;
    newMap.set(String(p.id), (newMap.get(String(p.id)) || 0) + Number(line.qty || 0));
  });
  const ids = new Set([...oldMap.keys(), ...newMap.keys()]), snapshot = [];
  ids.forEach(id => {
    const p = products.find(x => String(x.id) === id), oldQty = oldMap.get(id) || 0, newQty = newMap.get(id) || 0, delta = oldQty - newQty;
    if (p && delta) {
      const before = Number(p.stock || 0), after = before + delta;
      p.stock = after;
      recordInventoryMovement(history, user, 'product', p, delta, 'Correction commande ' + updated.orderNo, before, after, delta > 0 ? 'entry' : 'exit');
    }
    if (p && newQty) snapshot.push({ id: p.id, name: p.name, qty: newQty });
  });
  updated.inventorySnapshot = { products: snapshot, packaging: existing.inventorySnapshot?.packaging || [] };
  updated.inventoryDeducted = true; updated.inventoryDeductedAt = existing.inventoryDeductedAt;
  return { reconciled: true };
}

export function inventorySaveMessage(base, result) {
  if (!result || !result.deducted) return base;
  return result.warnings.length ? base + ' · stock déduit, vérifiez les alertes' : base + ' · stock mis à jour';
}
